import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { withAuth } from "@cdk-prod/fortellis-auth-context";
import RoleList from "../../UserProfileManager/RoleList";
import styled from "styled-components";
import withNavigation from "../../components/withNavigation";
import {
  IndeterminateProgressCircle,
  CardWrapper,
  CardBody,
  CardGroup,
  CardTitle,
  CardSubtitle,
  Button,
  Row,
  Column,
  Toast,
  TOAST_VARIANTS,
  TOAST_VISIBILITY_DURATIONS,
  TOAST_POSITIONS,
  BUTTON_SIZES,
  BUTTON_VARIANTS,
  BUTTON_ICON_COLORS,
  IconContentCopy,
} from "cdk-radial";
import config from "../../config/app.conf.json";
import CopyRolesDialog from "./CopyRolesDialog";

const ButtonContainer = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  gap: 16px;
  display: flex;
`;

const TOAST_TYPES = {
  SAVE_SUCCESS: {
    content: "Roles updated successfully.",
    variant: TOAST_VARIANTS.POSITIVE,
  },
  ASSIGNMENT_ERROR: {
    content: "Role assignment unsuccessful. Please try again",
    variant: TOAST_VARIANTS.NEGATIVE,
  },
  REVOKE_ERROR: {
    content: "Role removal unsuccessful. Please try again",
    variant: TOAST_VARIANTS.NEGATIVE,
  },
  POST_ERROR: {
    content: "Role update unsuccessful. Please try again.",
    variant: TOAST_VARIANTS.NEGATIVE,
  },
};

const USER_SEARCH_URL = config.api.user_uri;

function sortByRoleName(roleList) {
  return roleList.sort((a, b) => a.name.localeCompare(b.name));
}

function getDifferencesOfRoleLists(list1, list2) {
  const list1Ids = list1.map((role) => role.id);
  const list2Ids = list2.map((role) => role.id);

  const aDiffList = list1.filter((role) => !list2Ids.includes(role.id));
  const bDiffList = list2.filter((role) => !list1Ids.includes(role.id));

  return aDiffList.length > 0 || bDiffList.length > 0
    ? [aDiffList, bDiffList]
    : [];
}

function UserRoleProfile(props) {
  const [selectedRolesList, setSelectedRolesList] = useState([]);
  const [displayedRolesList, setDisplayedRolesList] = useState([]);
  const [dupUserMap, setDupUserMap] = useState({});
  const [activeToast, setActiveToast] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const canAssignRoles =
    props.auth.entitlements?.["*"]?.perm?.includes("assign");
  const userEmail = props.match.params.userEmail.toLowerCase();
  const userProfile = props.userProfile.profileDetails[userEmail];
  const globalDisable = props.userRoles.makingChanges;

  const userId = useMemo(
    () => userProfile?.userDetails?.id,
    [userProfile?.userDetails]
  );

  const userFetch = useMemo(
    () => props.userRoles.userFetches?.[userId],
    [props.userRoles.userFetches]
  );

  const userRolesList = useMemo(() => {
    if (!Array.isArray(userFetch?.userRolesList)) return [];

    const filtered = userFetch.userRolesList.filter(
      (role) => role.type === "platform"
    );

    return sortByRoleName(filtered);
  }, [userFetch?.userRolesList]);

  const allRolesList = useMemo(() =>
    sortByRoleName(props.userRoles.allRolesList || [])
  );

  const selectedAndAllDiff = useMemo(
    () => getDifferencesOfRoleLists(selectedRolesList, userRolesList),
    [selectedRolesList, userRolesList]
  );

  useEffect(() => {
    if (props.userRoles.isFetchedAllRoles && userFetch?.userRolesList) {
      const sorted = sortByRoleName(props.userRoles.allRolesList);
      setDisplayedRolesList(sorted);
      setSelectedRolesList(
        allRolesList.filter((role) =>
          userRolesList.some((userRole) => userRole.id === role.id)
        )
      );
    }
  }, [props.userRoles.isFetchedAllRoles, userFetch?.userRolesList]);

  useEffect(() => {
    if (props.auth.accessToken) {
      const {
        auth: { accessToken },
      } = props;

      props.getUserDetails(userEmail, accessToken);
      props.fetchAllRoles({ accessToken });
    }
  }, [props.auth.accessToken]);

  useEffect(() => {
    if (userProfile?.userDetails) {
      const userId = userProfile.userDetails.id;
      const {
        auth: { accessToken },
      } = props;

      props.fetchUserRoles({ uid: userId, accessToken });
    }
  }, [userProfile?.userDetails]);

  const searchForUsers = (text) => {
    return axios
      .get(USER_SEARCH_URL, {
        params: {
          q: text,
          status: "ACTIVE",
        },
        headers: {
          Authorization: `Bearer ${props.auth.accessToken}`,
        },
      })
      .then((res) => {
        return res.data.users.map((user) => {
          const userProfile = user.profile;

          return {
            firstName: userProfile.firstName,
            lastName: userProfile.lastName,
            email: userProfile.email,
            uid: user.uid,
          };
        });
      });
  };

  const handleCheckChange = (role) => {
    const roleId = role.id;
    setSelectedRolesList((prev) => {
      const prevSelectedRoleIds = prev.map((prevRole) => prevRole.id);

      const nextSelectedRolesList = prevSelectedRoleIds.includes(roleId)
        ? prev.filter((prevRole) => prevRole.id !== role.id)
        : [...prev, role];

      return nextSelectedRolesList;
    });
  };

  const handleSave = async () => {
    const [addedRolesList, revokedRolesList] = getDifferencesOfRoleLists(
      selectedRolesList,
      userRolesList
    );

    const addedRoleIds = addedRolesList.map((r) => r.id);
    const revokedRoleIds = revokedRolesList.map((r) => r.id);

    let hasAssignError = false;
    let hasRevokeError = false;

    if (addedRoleIds.length > 0) {
      const postResponse = await props.assignRolesToUser({
        uid: userProfile.userDetails.id,
        orgId: "*",
        orgName: "",
        roleIds: addedRoleIds,
        accessToken: props.auth.accessToken,
        reason: "User required new roles",
      });
      if (postResponse.apierror) {
        hasAssignError = true;
      }
    }

    if (revokedRoleIds.length > 0) {
      const postResponse = await props.revokeRolesFromUser({
        uid: userProfile.userDetails.id,
        orgId: "*",
        orgName: "",
        roleIds: revokedRoleIds,
        accessToken: props.auth.accessToken,
        reason: "User no longer needed some roles",
      });
      if (postResponse.apierror) {
        hasRevokeError = true;
      }
    }
    if (hasAssignError || hasRevokeError) {
      setActiveToast(
        hasAssignError
          ? hasRevokeError
            ? TOAST_TYPES.POST_ERROR
            : TOAST_TYPES.ASSIGNMENT_ERROR
          : TOAST_TYPES.REVOKE_ERROR
      );
      props.resetRoleErrors();
    } else {
      props.roleChangesMade({ uid: userProfile.userDetails.id });
      setActiveToast(TOAST_TYPES.SAVE_SUCCESS);
    }
  };

  const handleDupOpen = () => {
    setIsOpen(true);
  };

  const userRolesFetched =
    userId && props.userRoles.userFetches?.[userId]?.isFetchedUserRoles;

  if (
    userProfile?.isFetching ||
    !props.userRoles.isFetchedAllRoles ||
    !userRolesFetched
  ) {
    return (
      <div className="loading-container">
        <h4>Loading User Details</h4>
        <IndeterminateProgressCircle />
      </div>
    );
  }

  return (
    <>
      {activeToast && (
        <Toast
          content={activeToast.content}
          id="toast"
          onVisibilityDurationEnd={() => {
            setActiveToast(null);
          }}
          visibilityDuration={TOAST_VISIBILITY_DURATIONS.SIX_SECONDS}
          variant={activeToast.variant}
          position={TOAST_POSITIONS.FIXED}
        />
      )}
      <CardWrapper style={{ display: "flex", gap: "10px" }}>
        <CardBody style={{ padding: "0", gap: "36px" }}>
          <CardGroup style={{ padding: "16px 24px 0px 24px" }}>
            <Row>
              <Column>
                <CardTitle
                  style={{ fontSize: "48px" }}
                >{`${userProfile.userDetails.profile.firstName} ${userProfile.userDetails.profile.lastName}`}</CardTitle>
                <CardSubtitle style={{ fontSize: "16px" }}>
                  {userEmail}
                </CardSubtitle>
              </Column>
              <Column style={{ position: "relative" }}>
                <ButtonContainer>
                  <Button
                    dataTestId="copy-button"
                    id="copy-button"
                    text="Copy Roles"
                    icon={<IconContentCopy />}
                    iconColor={BUTTON_ICON_COLORS.PRIMARY}
                    variant={BUTTON_VARIANTS.TEXT}
                    onClick={handleDupOpen}
                  />
                  <Button
                    dataTestId="save-button"
                    id="save-button"
                    iconColor={BUTTON_SIZES.PRIMARY}
                    onClick={handleSave}
                    size={BUTTON_SIZES.STANDARD}
                    text="Save"
                    isDisabled={
                      selectedAndAllDiff.length === 0 || globalDisable
                    }
                  />
                </ButtonContainer>
              </Column>
            </Row>
          </CardGroup>
          <CardGroup>
            <RoleList
              selectedRolesList={selectedRolesList}
              allRolesList={allRolesList}
              setDisplayedRolesList={setDisplayedRolesList}
              sortByRoleName={sortByRoleName}
              globalDisable={globalDisable}
              displayedRolesList={displayedRolesList}
              handleCheckChange={handleCheckChange}
              canAssignRoles={canAssignRoles}
              noBorderRadius={true}
            />
          </CardGroup>
        </CardBody>
        <CopyRolesDialog
          fetchResults={searchForUsers}
          fetchUserRoles={props.fetchUserRoles}
          dupUserMap={dupUserMap}
          setDupUserMap={setDupUserMap}
          globalDisable={globalDisable}
          userRoles={props.userRoles}
          userProfile={userProfile}
          isCopyDialogOpen={isOpen}
          setIsCopyDialogOpen={setIsOpen}
          getDifferencesOfRoleLists={getDifferencesOfRoleLists}
          assignRolesToUser={props.assignRolesToUser}
          roleChangesMade={props.roleChangesMade}
          setActiveToast={setActiveToast}
        />
      </CardWrapper>
    </>
  );
}

const UserRoleProfileFn = withNavigation([
  {
    text: "Back to Home",
    goTo: "/",
  },
  {
    text: "Back to User Role Management",
    goTo: "/user-role-management",
  },
])(UserRoleProfile);

function UserRoleProfileContainer(props) {
  return props.auth.isAuthenticated ? (
    <UserRoleProfileFn {...props} />
  ) : (
    <div></div>
  );
}

export default withAuth(UserRoleProfileContainer);
